'use client';

import { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { addMonths, isBefore, isAfter } from 'date-fns';
import { HiOutlineBeaker, HiOutlineCalendar, HiOutlineCreditCard, HiOutlineLocationMarker } from 'react-icons/hi';
import { AiFillInfoCircle } from 'react-icons/ai';

import { EMAIL_REGEX, PHONE_REGEX } from 'lib/constants';

import { Trans, useTranslation } from 'src/i18n/client';
import useAppointments, { AppointmentType } from 'src/hooks/useAppointments';
import useRecaptcha from 'src/hooks/useRecaptcha';

import TimeSlotsView from './TimeSlotsView';

import Button from 'src/components/Button';
import FormError from 'src/components/FormError';
import DatePicker from 'src/components/DatePicker';
import useMyTestsState from 'src/hooks/useMyTestsState';
import SummaryItem from './SummaryItem';
import MyTestsSummary from 'src/components/MyTestsSummary';
import WizardSteps from './WizardSteps';
import TestSelection from './TestSelection';
import Spinner from 'src/components/Spinner';
import toast from 'src/components/TailwindToaster';
import { locationName, selectedTimeSlotSummary } from '../utils';
import useGlobalState from 'src/hooks/useGlobalState';
import Tooltip from 'src/components/Tooltip';

const getToday = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

export default function AppointmentsForm({ locale, location, noTestsBanner }) {
    const { t } = useTranslation(locale, 'common');
    const router = useRouter();
    const { user } = useGlobalState();
    const { isReady, generateToken } = useRecaptcha();
    const { locationsByCounty, setLocationsFilter, timeSlots, timeSlotsLoading, setTimeSlotsFilter, saveAppointment } =
        useAppointments();

    const [activeStep, setActiveStep] = useState(0);
    const [stateLocations, setStateLocations] = useState<any[]>([]);
    const [stateLocationsByCity, setStateLocationsByCity] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const { initializeTests, tests } = useMyTestsState();

    const scrollTo = (elementId) => {
        const step = document.getElementById(`step-${activeStep}`);
        const element = document.getElementById(elementId);
        if (!step || !element) return;
        step.scrollTo({
            top: element.offsetTop - step.offsetTop,
            behavior: 'smooth',
        });
    };

    const {
        getValues,
        control,
        formState: { isSubmitted },
        handleSubmit,
        watch,
        setValue,
        reset,
        trigger,
    } = useForm<AppointmentType>({
        defaultValues: {
            code: undefined,
            date: undefined,
            time_slot: undefined,
            state_code: location?.address?.city?.county?.code,
            location_code: location?.code || '',
            first_name: '',
            last_name: '',
            phone: '',
            notes: '',
            email: user?.email || '',
            email_confirm: user?.email || '',
            covid19_sampling: false,
            terms: false,
            locale: locale || 'ro',
        },
    });
    const watchFields = watch(['date', 'time_slot']);

    const updateStateLocations = (code?: string) => {
        const stateLocations = (locationsByCounty?.find((l) => l.code === code)?.locations || [])?.map((l) => ({
            code: l.code,
            name: locationName(l, true),
        }));

        const stateLocationsByCity = Object.values(
            (locationsByCounty?.find((l) => l.code === code)?.locations || [])?.reduce((acc, l: any) => {
                const key = l.address?.city?.name || ' ';
                if (!acc[key]) {
                    acc[key] = { name: key, locations: [] };
                }
                acc[key].locations.push({ code: l.code, name: locationName(l) });

                return acc;
            }, {}),
        );

        setStateLocations(stateLocations);
        setStateLocationsByCity(stateLocationsByCity);
    };

    useEffect(() => {
        if (locationsByCounty) {
            updateStateLocations(getValues('state_code'));
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationsByCounty]);

    const updateTimeSlots = useCallback(
        (date?: string, location_code?: string) => {
            setTimeSlotsFilter({
                date: date || '',
                location_code: location_code || '',
            });
        },
        [setTimeSlotsFilter],
    );

    useEffect(() => {
        setLocationsFilter({});
        updateTimeSlots(getValues('date'), getValues('location_code'));
    }, [setLocationsFilter, updateTimeSlots, getValues]);

    useEffect(() => {
        if (activeStep === 3) {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [activeStep]);

    const onSubmit = async (params: AppointmentType) => {
        try {
            setLoading(true);
            const recaptcha = await generateToken();
            await saveAppointment({
                ...params,
                recaptcha,
                tests: tests?.map((test) => test.code) || [],
            });

            await initializeTests([]);
            await router.replace(`/programare/succes`);
        } catch (error: any) {
            toast.error(
                error?.data?.code === 400009 ? t('appointments.changeTimeslot') : t('appointments.errorRegistering'),
            );
        } finally {
            setLoading(false);
        }
    };

    const handlePrev = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const handleNext = async () => {
        let valid = true;
        if (activeStep === 1) {
            valid = await trigger(['state_code', 'location_code', 'time_slot']);
        } else if (activeStep === 2) {
            valid = await trigger(['first_name', 'last_name', 'email', 'email_confirm', 'phone']);
        } else if (activeStep === 3) {
            valid = false;
        }

        if (valid) {
            setActiveStep(activeStep + 1);
        }
    };

    const classNameForStep = (index: number) => {
        const defaultClassName =
            'p-4 flex-1 overflow-x-hidden overflow-y-scroll lg:overflow-hidden lg:py-0 scroll-pt-4';
        return activeStep === index ? defaultClassName : `${defaultClassName} hidden lg:block`;
    };

    return (
        <div className="h-screen-nav-sm-dynamic md:h-screen-nav-md relative flex flex-col lg:h-auto">
            <WizardSteps activeStep={activeStep} locale={locale} onActiveStepSelected={setActiveStep} />

            <form
                method="POST"
                noValidate
                className="flex flex-1 flex-col overflow-x-hidden overflow-y-scroll lg:block lg:overflow-hidden lg:py-10"
                onSubmit={handleSubmit(onSubmit)}>
                <div id="step-0" className={classNameForStep(0)}>
                    <h1 className="mb-4 text-4xl md:mb-10 md:text-6xl xl:text-7xl">Programare online</h1>

                    <div className="bg-light-green -mx-4 mb-4 p-4 text-xs md:mx-0 md:text-sm">
                        <Trans t={t} i18nKey="common:appointments.alertCNAS" components={{ b: <b /> }} />
                    </div>
                    <TestSelection renderFor="mobile" noTestsBanner={noTestsBanner} />
                </div>

                <div id="step-1" className={classNameForStep(1)}>
                    <h2 className="mb-4 text-2xl lg:mt-4">{t('appointments.steps.location')}</h2>

                    <div className="mb-2 flex flex-col gap-4 md:flex-row">
                        <Controller
                            name="state_code"
                            control={control}
                            rules={{
                                required: t('appointments.locationRequired'),
                            }}
                            render={({ field: { value, onChange }, fieldState }) => (
                                <div className="flex-1">
                                    <select
                                        className={fieldState.error ? 'form-error' : ''}
                                        value={value}
                                        disabled={!locationsByCounty?.length}
                                        onChange={(value) => {
                                            onChange(value);
                                            updateStateLocations(value?.target?.value);
                                            setValue('location_code', '');
                                        }}>
                                        <option value="">Selectează Județul</option>
                                        {locationsByCounty?.map((l) => (
                                            <option key={l.code} value={l.code}>
                                                {l.name}
                                            </option>
                                        ))}
                                    </select>
                                    <FormError error={fieldState.error} />
                                </div>
                            )}
                        />
                        <Controller
                            name="location_code"
                            control={control}
                            rules={{
                                required: t('appointments.locationRequired'),
                            }}
                            render={({ field: { value, onChange }, fieldState }) => (
                                <div className="flex-1">
                                    <select
                                        id="location-select"
                                        className={fieldState.error ? 'form-error' : ''}
                                        value={value}
                                        disabled={!stateLocations?.length}
                                        onChange={(value) => {
                                            setValue('time_slot', '');
                                            updateTimeSlots(getValues('date'), value?.target?.value);
                                            onChange(value);
                                        }}>
                                        <option value="">Selectează o locație</option>
                                        {stateLocationsByCity?.map((slc) => (
                                            <optgroup key={slc.name} label={slc.name}>
                                                {slc.locations?.map((l) => (
                                                    <option key={l.code} value={l.code}>
                                                        {l.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                    <FormError error={fieldState.error} />
                                </div>
                            )}
                        />
                    </div>

                    <h2 className="mb-4 text-2xl lg:mt-8" id="date-picker-container">
                        {t('appointments.steps.dateTime')}
                    </h2>

                    <div className="flex flex-col gap-4 md:flex-row md:pb-0">
                        <div className="flex-1">
                            <Controller
                                name="date"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <DatePicker
                                        value={value}
                                        disabledDate={(date: any) => {
                                            const today = getToday();
                                            return isBefore(date, today) || isAfter(date, addMonths(today, 1));
                                        }}
                                        onChange={(value) => {
                                            setValue('time_slot', '');
                                            onChange(value);
                                            updateTimeSlots(value, getValues('location_code') || '');
                                            if (value) scrollTo('time-slots-container');
                                        }}
                                    />
                                )}
                            />
                        </div>
                        <div className="min-h-[200px] flex-1" id="time-slots-container">
                            <Controller
                                name="time_slot"
                                control={control}
                                rules={{
                                    required: t('appointments.timeSlotRequired'),
                                }}
                                render={({ field: { value, onChange }, fieldState }) => (
                                    <>
                                        <TimeSlotsView
                                            disabled={!(getValues('location_code') && getValues('date'))}
                                            locale={locale}
                                            items={timeSlots}
                                            loading={timeSlotsLoading}
                                            selected={value || ''}
                                            onSelect={onChange}
                                        />
                                        <FormError error={fieldState.error} />
                                    </>
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div id="step-2" className={classNameForStep(2)}>
                    <h2 className="mb-3 text-2xl lg:mt-8">{t('appointments.steps.personalDetails')}</h2>

                    <div className="mb-2 text-xs md:text-sm">{t('appointments.singlePerson')}</div>

                    <div className="mb-2 flex grid-cols-2 flex-col gap-4 md:grid">
                        <Controller
                            name="first_name"
                            control={control}
                            rules={{
                                required: t('appointments.firstNameRequired'),
                            }}
                            render={({ field: { value, onChange }, fieldState }) => (
                                <div className="flex-1">
                                    <input
                                        className={fieldState.error ? 'form-error' : ''}
                                        type="text"
                                        placeholder={t('appointments.firstName')}
                                        value={value}
                                        onChange={onChange}
                                    />
                                    <FormError error={fieldState.error} />
                                </div>
                            )}
                        />
                        <Controller
                            name="last_name"
                            control={control}
                            rules={{
                                required: t('appointments.nameRequired'),
                            }}
                            render={({ field: { value, onChange }, fieldState }) => (
                                <div className="flex-1">
                                    <input
                                        className={fieldState.error ? 'form-error' : ''}
                                        type="text"
                                        placeholder={t('appointments.name')}
                                        value={value}
                                        onChange={onChange}
                                    />
                                    <FormError error={fieldState.error} />
                                </div>
                            )}
                        />

                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                required: t('appointments.emailRequired'),
                                pattern: {
                                    value: EMAIL_REGEX,
                                    message: t('appointments.emailInvalid'),
                                },
                            }}
                            render={({ field: { value, onChange }, fieldState }) => (
                                <div className="flex-1">
                                    <div className="flex items-center">
                                        <input
                                            disabled={!!user}
                                            className={fieldState.error ? 'form-error' : ''}
                                            type="email"
                                            placeholder={t('appointments.email')}
                                            value={value}
                                            onChange={onChange}
                                        />
                                        {!!user && (
                                            <div className="ml-4">
                                                <Tooltip
                                                    position="left"
                                                    title={
                                                        <Trans t={t} i18nKey="common:appointments.alertEmailDisabled" />
                                                    }>
                                                    <AiFillInfoCircle />
                                                </Tooltip>
                                            </div>
                                        )}
                                    </div>

                                    <FormError error={fieldState.error} />
                                </div>
                            )}
                        />

                        <Controller
                            name="email_confirm"
                            control={control}
                            rules={{
                                validate: (value) => value === getValues('email') || t('appointments.emailMismatch'),
                            }}
                            render={({ field: { value, onChange }, fieldState }) => (
                                <div className="flex-1">
                                    <input
                                        className={fieldState.error ? 'form-error' : ''}
                                        type={!user ? 'email' : 'hidden'}
                                        placeholder={t('appointments.emailConfirm')}
                                        value={value}
                                        onChange={onChange}
                                    />
                                    <FormError error={fieldState.error} />
                                </div>
                            )}
                        />

                        <Controller
                            name="phone"
                            control={control}
                            rules={{
                                required: t('appointments.phoneRequired'),
                                pattern: {
                                    value: PHONE_REGEX,
                                    message: t('appointments.phoneInvalid'),
                                },
                            }}
                            render={({ field: { value, onChange }, fieldState }) => (
                                <div className="flex-1">
                                    <input
                                        type="tel"
                                        className={fieldState.error ? 'form-error' : ''}
                                        placeholder={t('appointments.phone')}
                                        value={value}
                                        onChange={onChange}
                                    />
                                    <FormError error={fieldState.error} />
                                </div>
                            )}
                        />

                        <Controller
                            name="notes"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <input
                                    type="text"
                                    className="flex-1"
                                    placeholder={t('appointments.notes')}
                                    value={value}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </div>
                </div>

                <div id="step-3" className={classNameForStep(3)}>
                    <h2 className="mb-3 text-2xl lg:mt-8">{t('appointments.steps.summary')}</h2>
                    <div className="mb-4 flex flex-col gap-4 bg-white p-4 text-base">
                        <SummaryItem icon={<HiOutlineBeaker />} title={t('appointments.summary.tests')}>
                            <MyTestsSummary asContent={true} />
                        </SummaryItem>
                        <SummaryItem icon={<HiOutlineLocationMarker />} title={t('appointments.summary.location')}>
                            {stateLocations?.find((l) => l.code === getValues('location_code'))?.name}
                        </SummaryItem>
                        <SummaryItem icon={<HiOutlineCalendar />} title={t('appointments.summary.dateTime')}>
                            {selectedTimeSlotSummary(getValues('date'), getValues('time_slot'))}
                        </SummaryItem>
                        <SummaryItem icon={<HiOutlineCreditCard />} title={t('appointments.summary.payment')}>
                            {t('appointments.summary.paymentDescription')}
                        </SummaryItem>
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="terms"
                            control={control}
                            rules={{
                                required: t('appointments.termsRequired'),
                            }}
                            render={({ field: { value, onChange }, fieldState }) => (
                                <>
                                    <label>
                                        <input type="checkbox" checked={value} onChange={onChange} className="mr-3" />
                                        <Trans
                                            t={t}
                                            i18nKey="common:appointments.terms"
                                            components={{
                                                link: (
                                                    <Link
                                                        className="text-primary underline hover:no-underline"
                                                        href="/termeni-si-conditii-platforma-programari-online"
                                                    />
                                                ),
                                            }}
                                        />
                                    </label>
                                    <FormError error={fieldState.error} />
                                </>
                            )}
                        />
                    </div>
                    <div className="my-4 text-xs">
                        <Trans
                            t={t}
                            i18nKey="common:appointments.gdprInfo"
                            components={{
                                link: (
                                    <Link
                                        className="text-primary underline hover:no-underline"
                                        href="/protectia-datelor-cu-caracter-personal"
                                    />
                                ),
                            }}
                        />
                    </div>
                </div>

                <div className="drop-shadow-t-lg lg:bg-lightest-grey bg-white p-4 lg:drop-shadow-none">
                    {activeStep < 3 && (
                        <div className="flex items-center gap-4 lg:hidden">
                            {activeStep > 0 && (
                                <Button variant="text" className="flex-1 cursor-pointer" onClick={handlePrev}>
                                    {t('appointments.backButton')}
                                </Button>
                            )}
                            <Button variant="primary" className="flex-1 cursor-pointer" onClick={handleNext}>
                                {t('appointments.continueButton')}
                            </Button>
                        </div>
                    )}

                    <Button
                        type="submit"
                        variant="primary"
                        className={`${activeStep !== 3 ? 'hidden' : ''} w-full lg:block lg:w-1/3`}
                        disabled={!isReady}>
                        {t('appointments.completeButton')}
                    </Button>
                </div>
            </form>

            {loading && (
                <div className="bg-dark-green/50 absolute top-0 left-0 flex h-full w-full items-center justify-center">
                    <Spinner variant="white" />
                </div>
            )}
        </div>
    );
}
